import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import {
  calculateCartTotalItems,
  CART_ACTIONS,
  calculateCartTotal,
  getCartItemQuantity as getMarketCartItemQuantity,
  isItemInCart as isMarketItemInCart,
  reduceCartItems,
} from "domain/market/cart";
import { readCartItems, writeCartItems } from "domain/market/cart-storage";

export default function useCartState() {
  const [items, dispatchItems] = useReducer(reduceCartItems, []);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const totalItems = useMemo(() => calculateCartTotalItems(items), [items]);
  const totalPrice = useMemo(() => calculateCartTotal(items), [items]);

  useEffect(() => {
    dispatchItems({ type: CART_ACTIONS.hydrate, items: readCartItems() });
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    if (!isLoaded) {
      return;
    }

    writeCartItems(items);
  }, [items, isLoaded]);

  const addItem = useCallback((item, quantity = 1) => {
    dispatchItems({ type: CART_ACTIONS.addItem, item, quantity });
  }, []);

  const removeItem = useCallback((itemId) => {
    dispatchItems({ type: CART_ACTIONS.removeItem, itemId });
  }, []);

  const removeItemQuantity = useCallback((itemId, quantity = null) => {
    dispatchItems({ type: CART_ACTIONS.removeItemQuantity, itemId, quantity });
  }, []);

  const updateQuantity = useCallback((itemId, quantity) => {
    dispatchItems({ type: CART_ACTIONS.updateQuantity, itemId, quantity });
  }, []);

  const clearCart = useCallback(() => {
    dispatchItems({ type: CART_ACTIONS.clear });
  }, []);

  const isInCart = useCallback(
    (itemId) => isMarketItemInCart(items, itemId),
    [items],
  );

  const getItemQuantity = useCallback(
    (itemId) => getMarketCartItemQuantity(items, itemId),
    [items],
  );

  const openCart = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeCart = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleCart = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  return useMemo(
    () => ({
      items,
      isOpen,
      totalItems,
      totalPrice,
      addItem,
      removeItem,
      removeItemQuantity,
      updateQuantity,
      clearCart,
      isInCart,
      getItemQuantity,
      openCart,
      closeCart,
      toggleCart,
    }),
    [
      items,
      isOpen,
      totalItems,
      totalPrice,
      addItem,
      removeItem,
      removeItemQuantity,
      updateQuantity,
      clearCart,
      isInCart,
      getItemQuantity,
      openCart,
      closeCart,
      toggleCart,
    ],
  );
}
